import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";

import { withStyles } from "@material-ui/core/styles";
import {
  useMediaQuery,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Dialog,
  Avatar,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Select,
  Chip,
  Backdrop,
  CircularProgress,
} from "@material-ui/core";

import MainLayout from "../MainLayout";
import OrderAddressMap from "./OrderAdressMap";
import { formatDate, calcDateDiff, getStatusLabel } from "../utils";
import { baseUrl, currentRole, Roles, orderStatus } from "../config";
import useStyles from "./styles";

const StyledTableRow = withStyles(() => ({
  root: {
    "&:nth-of-type(even)": {
      backgroundColor: "#F4F6F8",
    },
  },
}))(TableRow);

const StyledTableHead = withStyles(() => ({
  root: {
    backgroundColor: "#FAFAFA",
  },
}))(TableHead);

const ErrorButton = withStyles(() => ({
  root: {
    color: "#f44336",
    border: "1px solid rgba(244, 143, 177, 0.5)",
  },
}))(Button);

const ProductCount = withStyles(() => ({
  root: {
    color: "#fff",
    backgroundColor: "#5D3EBD",
    marginRight: 10,
    fontWeight: 600,
  },
}))(Chip);

export default function OrderDetail() {
  const matches = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  let { orderId } = useParams();
  const classes = useStyles();
  const { register, errors, handleSubmit } = useForm();

  const [order, setOrder] = useState(null);
  const [warehouseLocation, setWarehouseLocation] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // GET: Order Detail
  async function getOrder() {
    setLoading(true);
    const res = await fetch(`${baseUrl(currentRole)}/orders/${orderId}`, {
      headers: {
        Authorization: localStorage.getItem("authToken"),
      },
    });

    res
      .json()
      .then((res) => {
        setOrder(res.order ? (res.order !== null ? res.order : null) : res);
        if (order && order.location) {
          getWarehouseLocation(
            order.location.coordinates[0],
            order.location.coordinates[1]
          );
        }
      })
      .then(() => setLoading(false))
      .catch((err) => console.log(err));
  }

  // POST: Prepare order action by picker
  async function prepareOrder() {
    setLoading(true);

    const res = await fetch(
      `${baseUrl(currentRole)}/orders/${orderId}/prepare`,
      {
        headers: {
          Authorization: localStorage.getItem("authToken"),
        },
        method: "POST",
      }
    );

    res
      .json()
      .then(() => window.location.reload())
      .then(() => setLoading(false))
      .catch((err) => console.log(err));
  }

  async function getWarehouseLocation(lat, long) {
    const res = await fetch(
      `${baseUrl(Roles.MH)}/warehouse-control?lat=${lat}&long=${long}`,
      {
        headers: {
          Authorization: localStorage.getItem("authToken"),
        },
      }
    );

    res
      .json()
      .then((res) => {
        setLoading(false);
        if (res.warehouse) {
          setWarehouseLocation(res.warehouse.location);
        }
      })
      .catch((err) => console.log(err));
  }

  // POST: Receive order action by courier
  async function receiveOrder() {
    setLoading(true);

    const res = await fetch(
      `${baseUrl(currentRole)}/orders/${orderId}/receive`,
      {
        headers: {
          Authorization: localStorage.getItem("authToken"),
        },
        method: "POST",
        body: JSON.stringify({
          location: warehouseLocation || [],
        }),
      }
    );

    res
      .json()
      .then(() => window.location.reload())
      .then(() => setLoading(false))
      .catch((err) => console.log(err));
  }

  // POST: Deliver order action by courier
  async function deliverOrder() {
    setLoading(true);

    const res = await fetch(
      `${baseUrl(currentRole)}/orders/${orderId}/deliver`,
      {
        headers: {
          Authorization: localStorage.getItem("authToken"),
        },
        method: "POST",
        body: JSON.stringify({
          location: order.location,
        }),
      }
    );

    res
      .json()
      .then(() => window.location.reload())
      .then(() => setLoading(false))
      .catch((err) => console.log(err));
  }

  // POST: Cancel order action
  async function cancelOrder(data) {
    setLoading(true);

    data.cancelStatus = Number(data.cancelStatus);

    const res = await fetch(
      `${baseUrl(currentRole)}/orders/${orderId}/cancel`,
      {
        headers: {
          Authorization: localStorage.getItem("authToken"),
        },
        method: "POST",
        body: JSON.stringify(data),
      }
    );

    res
      .json()
      .then(() => window.location.reload())
      .then(() => setCancelModalOpen(false))
      .then(() => setLoading(false))
      .catch((err) => console.log(err));
  }

  async function printPDF() {
    let byteCharacters = atob(order.documentList[0].documentData);
    let byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    let byteArray = new Uint8Array(byteNumbers);
    let file = new Blob([byteArray], {type: 'application/pdf;base64'});
    let fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  }

  useEffect(() => {
    getOrder();
  }, [orderId]);

  const hasResult = !isLoading && order;

  const onCancelSubmit = (values) => cancelOrder(values);

  return (
    <MainLayout>
      <Backdrop className={classes.backdrop} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {hasResult && (
        <Grid container justify="space-between" alignItems="flex-end">
          <Grid
            container
            className={classes.titleContainer}
            justify="space-between"
            alignItems="flex-end"
          >
            <Grid item xs={12} xl={6} md={6}>
              <Typography variant="overline" display="block" gutterBottom>
                Sipariş Id
              </Typography>
              <Typography variant="h3" gutterBottom>
                {order.conversationId}
              </Typography>
            </Grid>
            <Grid item xs={12} xl={6} md={6}>
              <Grid
                container
                justify="flex-end"
                alignItems="center"
                spacing={3}
              >
                {!matches && (
                  <Button
                    component={Link}
                    to="/orders"
                    variant="contained"
                    color="primary"
                  >
                    Aktif siparişlere dön
                  </Button>
                )}

                {Roles.PICKER === currentRole && order.status !== orderStatus.CANCELLED && (
                  <Grid item component={Box} displayPrint="none">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      onClick={printPDF}
                    >
                      Yazdır
                    </Button>
                  </Grid>
                )}

                {Roles.PICKER === currentRole && (
                  <Grid item>
                    {order.status === orderStatus.CREATED && (
                      <Button
                        onClick={prepareOrder}
                        variant="outlined"
                        color="primary"
                        size="large"
                      >
                        Siparişi Hazırla
                      </Button>
                    )}
                  </Grid>
                )}

                {Roles.COURIER === currentRole && (
                  <Grid item>
                    {order.status === orderStatus.PREPARED && (
                      <Button
                        onClick={receiveOrder}
                        variant="outlined"
                        color="primary"
                        size="large"
                      >
                        Siparişi Al
                      </Button>
                    )}
                  </Grid>
                )}

                {Roles.COURIER === currentRole && (
                  <Grid item>
                    {order.status !== orderStatus.DELIVERED && (
                      <Button
                        onClick={deliverOrder}
                        variant="outlined"
                        color="primary"
                        size="large"
                      >
                        Siparişi Teslim Et
                      </Button>
                    )}
                  </Grid>
                )}

                <Grid item>
                  {order.status !== orderStatus.CANCELLED && (
                    <ErrorButton
                      variant="outlined"
                      size="large"
                      onClick={() => setCancelModalOpen(true)}
                    >
                      Siparişi İptal Et
                    </ErrorButton>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {order && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4} xl={3}>
                <Paper component={Card}>
                  <CardHeader
                    title={
                      <Typography variant="h5" display="block">
                        Sipariş Bilgisi
                      </Typography>
                    }
                  />
                  <Divider />
                  <CardContent className={classes.cardContent}>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            Geçen Zaman
                          </TableCell>
                          <TableCell align="right">
                            {orderStatus.DELIVERED === order.status ||
                            orderStatus.CANCELLED === order.status
                              ? "-"
                              : calcDateDiff(order.createdAt)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            Ad Soyad
                          </TableCell>
                          <TableCell align="right">
                            {`${order.name}  ${order.lastName}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            Telefon No
                          </TableCell>
                          <TableCell align="right">
                            {order.phoneNumber}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            İl / İlçe
                          </TableCell>
                          <TableCell align="right">{`${order.city} / ${order.district} `}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            Bina No
                          </TableCell>
                          <TableCell align="right">
                            {order.buildingNumber}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            Kat. No
                          </TableCell>
                          <TableCell align="right">
                            {order.floorNumber}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            Daire No
                          </TableCell>
                          <TableCell align="right">{order.aptNumber}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            Açık Adres
                          </TableCell>
                          <TableCell align="right">
                            {order.addressDirections}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row">
                            Tarih
                          </TableCell>
                          <TableCell align="right">
                            {formatDate(order.createdAt)}
                          </TableCell>
                        </TableRow>
                        {currentRole === Roles.GETIR_MH && (
                          <>
                            <TableRow>
                              <TableCell component="th" scope="row">
                                Depo
                              </TableCell>
                              <TableCell align="right">
                                {order.warehouseName}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell component="th" scope="row">
                                Kurye
                              </TableCell>
                              <TableCell align="right">
                                {order.courierName}
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                        <TableRow>
                          <TableCell component="th" scope="row">
                            Durum
                          </TableCell>
                          <TableCell align="right">
                            {getStatusLabel(order.status)}
                          </TableCell>
                        </TableRow>
                        {orderStatus.CANCELLED === order.status && (
                          <TableRow>
                            <TableCell component="th" scope="row">
                              İptal Nedeni
                            </TableCell>
                            <TableCell align="right">
                              {order.cancelReason}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Paper>
              </Grid>
              <Grid item xs={12} md={8} xl={9}>
                {order.location && (
                  <OrderAddressMap location={order.location} />
                )}
              </Grid>
            </Grid>
          )}
          <Dialog
            fullWidth
            open={cancelModalOpen}
            onClose={() => setCancelModalOpen(false)}
          >
            <form noValidate onSubmit={handleSubmit(onCancelSubmit)}>
              <DialogTitle id="form-dialog-title">
                Siparişi iptal et
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Lütfen siparişi iptal etme nedenini belirtiniz
                </DialogContentText>
                <TextField
                  autoFocus
                  margin="dense"
                  id="cancelReason"
                  name="cancelReason"
                  label="İptal Nedeni"
                  fullWidth
                  inputRef={register({
                    required: true,
                  })}
                  required
                  error={errors.cancelReason}
                  helperText={
                    errors.cancelReason && "Lütfen iptal nedenini belirtiniz"
                  }
                />
                <Select
                  native
                  defaultValue="50"
                  variant={"outlined"}
                  fullWidth
                  inputProps={{
                    name: "cancelStatus",
                    id: "cancel-status",
                  }}
                  inputRef={register({
                    required: true,
                  })}
                  required
                  error={errors.cancelReason}
                  helperText={
                    errors.cancelReason && "Lütfen iptal nedenini belirtiniz"
                  }
                >
                  <option value="42">Adreste Tanınmıyor</option>
                  <option value="46">İade Geçerli Kimlik Gösterilmedi</option>
                  <option value="50">İptal</option>
                  <option value="7">Evde Yok</option>
                  <option value="74">Eksik Evrak</option>
                  <option value="8">Adres Hatalı / Eksik / Yetersiz</option>
                </Select>
              </DialogContent>
              <DialogActions>
                <Grid container justify="space-between">
                  <Grid item>
                    <Button
                      onClick={() => setCancelModalOpen(false)}
                      color="primary"
                      variant="outlined"
                    >
                      Vazgeç
                    </Button>
                  </Grid>
                  <Grid item>
                    <ErrorButton type="submit" variant="outlined">
                      Siparişi İptal Et
                    </ErrorButton>
                  </Grid>
                </Grid>
              </DialogActions>
            </form>
          </Dialog>
        </Grid>
      )}
    </MainLayout>
  );
}
